import { CellContext } from '@tanstack/react-table';
import { useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import clsx from 'clsx';
import AppCountDown, { AppCountDownTick } from 'src/components/AppCountDown/AppCountDown';
import DateTimeText from 'src/components/DateTimeText/DateTimeText';
import appMoment from 'src/helpers/app-moment';
import { formatPrice } from 'src/helpers/price';
import useVendorData from 'src/services/hooks/useVendorData';
import * as yup from 'yup';
import { FlashDiscountPeriodTypeResponse, FlashDiscountProductListItem } from '../services/models/flash-discount';

interface IProps {
  period: FlashDiscountPeriodTypeResponse & { colors: [string, string] };
  cellContext: CellContext<FlashDiscountProductListItem, unknown>;
  showSimulatedDiscountPrice?: boolean;
}

const generateValidationSchema = (originalPrice: number) => {
  return yup.object().shape({
    price: yup
      .number()
      .min(10, `En az ${formatPrice(10, { suffix: ' TL' })} ve üstü olmalıdır.`)
      .max(originalPrice, `Fiyat ${formatPrice(originalPrice, { suffix: ' TL' })}’den büyük olamaz.`)
      .required(),
  });
};

const CustomInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return <input {...props} />;
};

export default function FlashProductsPeriodCell(props: IProps) {
  const { period, cellContext: ctx, showSimulatedDiscountPrice = false } = props;
  const { _checkField } = useVendorData();

  const periodData = (ctx?.row?.original?.periodInfo || []).find((value) => value?.periodTypeId === period?.id);

  const uniqueId = `period-${ctx?.row?.original?.id}-${periodData?.id}`;

  const tableTempData = ctx?.table?.options?.meta?.getTableTempData?.({}) || {};
  const { selectedRowId = null, selectedPeriodId = null, editPriceData = {}, loading = false } = tableTempData;

  const originalPrice = periodData?.suggestedPrice || 0;

  const validationSchema = generateValidationSchema(originalPrice);

  const rowId = ctx?.row?.original?.id || ctx?.row?.id;
  const isSelected = selectedRowId === rowId && selectedPeriodId === periodData?.id;

  const { price = originalPrice, errors = {} } = (isSelected ? editPriceData : {}) || {};

  const lastAcceptDate = useMemo(() => {
    return appMoment(periodData?.lastAcceptTime);
  }, [periodData]);

  const isAvailable = useMemo(() => {
    return appMoment(lastAcceptDate).isBefore(appMoment());
  }, [lastAcceptDate]);

  const disableForm = useMemo(() => {
    return loading || isAvailable || !_checkField('flashSaleProduct', true);
  }, [loading, isAvailable, _checkField]);

  const handleSelectionChange = () => {
    ctx?.table?.options?.meta?.setTableTempData?.(
      {
        selectedRowId: rowId,
        selectedPeriodId: periodData?.id,

        editPriceData: {
          isValid: true,
          errors: {},
          price: originalPrice,
        },
      },
      { merge: false },
    );
  };

  const handlePriceChange = async (value: number) => {
    if (!isSelected) return;

    const result = { isValid: true, errors: {}, price: value };

    try {
      await validationSchema.validate({ price: value }, { abortEarly: false });

      result.isValid = true;
      result.errors = {};
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        const formattedErrors: Record<string, string> = {};

        error.inner.forEach((err) => {
          if (err.path && !formattedErrors[err.path]) {
            formattedErrors[err.path] = err.message;
          }
        });

        result.isValid = false;
        result.errors = formattedErrors;
      } else {
        result.isValid = false;
        result.errors = { price: error.message };
      }
    }

    ctx?.table?.options?.meta?.setTableTempData?.({
      editPriceData: result,
    });
  };

  if (!periodData) return <span className="text-muted">-</span>;
  return (
    <div className="d-flex flex-column justify-content-center w-fit">
      {/** Date Range */}
      <div
        className="d-flex gap-2 w-fit fw-bold"
        style={{
          backgroundColor: period?.colors?.[0],
          padding: '6px 8px',
          borderRadius: '4px',
          color: period?.colors?.[1],
        }}
      >
        <DateTimeText value={appMoment.parseZone(periodData.startAt)} />
        <span>-</span>
        <DateTimeText value={appMoment.parseZone(periodData.endAt)} />
      </div>

      {/** Selection */}
      <div className="form-check form-check-custom form-check-solid form-check-sm w-100">
        <input
          id={uniqueId}
          className="form-check-input flex-shrink-0"
          type="radio"
          name={`period-${ctx?.row?.original?.id}`}
          value={period?.id}
          checked={isSelected}
          onChange={() => {
            if (disableForm) return;
            handleSelectionChange();
          }}
          disabled={disableForm}
        />
        <label className="form-check-label w-100 py-4" htmlFor={uniqueId}>
          Seç
        </label>
      </div>

      {/** Remaining Time */}
      <div className="d-flex gap-2 ">
        <span className="fw-bold">Kalan Süre:</span>
        <AppCountDown
          tooltip={false}
          endDate={lastAcceptDate}
          expiredTextClassName="text-danger"
          format={({ helpers }) => {
            // Bir günden büyükse, gün ve saati göster
            if (!helpers.lessThan.day) return `{[D] gün} {[H] saat}`;
            // Bir saatten büyükse, saati ve dakikayı göster
            if (!helpers.lessThan.hour) return `{[H] saat} {[m] dakika}`;
            // Bir dakikadan büyükse, dakikayı ve saniyeyi göster
            else if (!helpers.lessThan.minute) return `{[m] dakika} {[s] saniye}`;
            // Bir saniyeden büyükse, saniyeyi göster
            else if (!helpers.lessThan.second) return `{[s] saniye}`;

            return '-';
          }}
          tick={({ remainingTimeMs, helpers }) => {
            // Bir günden büyükse timer ekleme
            if (!helpers.lessThan.day) return false;
            // Bir saatten büyükse dakikada bir çalışacak timer ekle
            if (!helpers.lessThan.hour) return AppCountDownTick.MINUTES;
            // Diğer durumlarda ve kalan süre 0'dan büyükse saniyede bir çalışacak timer ekle
            if (remainingTimeMs > 0) return AppCountDownTick.SECONDS;

            // Yoksa timer ekleme
            return false;
          }}
        />
      </div>

      {/** Price Input */}
      <div className="d-flex flex-column gap-2" style={{ minWidth: 280 }}>
        <NumericFormat
          value={price}
          max={originalPrice}
          min={0}
          suffix={' ₺'}
          thousandSeparator="."
          decimalSeparator=","
          customInput={CustomInput}
          allowNegative={false}
          className={clsx('form-control form-control-sm', {
            'is-invalid border-danger': errors?.price,
          })}
          disabled={!isSelected || disableForm}
          onValueChange={(values) => {
            if (disableForm) return;
            handlePriceChange(Number(values.value));
          }}
        />

        {showSimulatedDiscountPrice && periodData?.simulationDiscountPrice ? (
          <div className="d-flex gap-2 w-fit" style={{ fontSize: '12px' }}>
            <span className="fw-bold w-fit flex-shrink-0">Önerilen Fiyata Göre Kampanyalı Fiyat:</span>
            <span className="w-fit flex-shrink-0">{formatPrice(periodData.simulationDiscountPrice, { suffix: ' TL' })}</span>
          </div>
        ) : null}

        <div className="d-flex flex-column" style={{ height: 16 }}>
          {errors?.price ? (
            <div className="text-danger" style={{ fontSize: '12px', maxWidth: 280 }}>
              {errors?.price}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
